import { paymentStatementApi } from './base';

export interface Domain {
	id: string;
	tenant_id: string;
	domain: string;
	domain_type: 'subdomain' | 'custom' | 'alias';
	is_primary: boolean;
	verification_status: 'pending' | 'verified' | 'failed';
	verification_token?: string;
	verification_method?: string;
	verified_at?: string;
	last_verification_attempt?: string;
	cloudfront_status: 'pending' | 'provisioning' | 'active' | 'failed';
	cloudfront_provisioned_at?: string;
	acm_certificate_arn?: string;
	acm_certificate_status?:
		| 'pending'
		| 'issued'
		| 'validation_timed_out'
		| 'failed';
	acm_certificate_validation_records?: {
		name: string;
		type: string;
		value: string;
	};
	created_at: string;
	updated_at?: string;
	metadata?: Record<string, any>;
}

export interface DomainVerificationInstructions {
	domain: string;
	verification_token: string;
	cname_record: {
		name: string;
		type: string;
		value: string;
		ttl: number;
	};
	txt_record: {
		name: string;
		type: string;
		value: string;
		ttl: number;
	};
	cloudfront_domain: string;
	instructions: {
		step1: string;
		step1_details: {
			name: string;
			type: string;
			value: string;
			ttl: number;
		};
		step2: string;
		step2_details: {
			name: string;
			type: string;
			value: string;
			ttl: number;
		};
		step3: string;
		note: string;
	};
}

export interface DomainVerificationResult {
	success: boolean;
	message: string;
	domain: Domain;
	verification_results?: {
		cname_verified: boolean;
		txt_verified: boolean;
		fully_verified: boolean;
	};
}

export interface CertificateValidationInstructions {
	domain: string;
	certificate_arn?: string;
	certificate_status: string;
	validation_records: {
		name: string;
		type: string;
		value: string;
	};
	instructions: {
		step1: string;
		step2: string;
		step3: string;
	};
}

export interface DomainsResponse {
	success: boolean;
	domains: Domain[];
}

export const domainsApi = paymentStatementApi.injectEndpoints({
	endpoints: (builder) => ({
		getTenantDomains: builder.query<DomainsResponse, string>({
			query: (tenantId) => ({
				url: `/domains/tenant/${tenantId}`,
				method: 'GET',
			}),
			providesTags: (result, error, tenantId) => [
				{ type: 'Domains', id: tenantId },
			],
		}),

		getVerificationInstructions: builder.query<
			{ success: boolean } & DomainVerificationInstructions,
			string
		>({
			query: (domainId) => ({
				url: `/domains/${domainId}/verification-instructions`,
				method: 'GET',
			}),
		}),

		verifyDomain: builder.mutation<DomainVerificationResult, string>({
			query: (domainId) => ({
				url: `/domains/${domainId}/verify`,
				method: 'POST',
			}),
			invalidatesTags: (result, error, domainId) => [
				{ type: 'Domains', id: result?.domain.tenant_id },
			],
		}),

		getCertificateValidationInstructions: builder.query<
			{ success: boolean } & CertificateValidationInstructions,
			string
		>({
			query: (domainId) => ({
				url: `/domains/${domainId}/certificate-validation`,
				method: 'GET',
			}),
		}),

		checkCertificateAndUpdateCloudFront: builder.mutation<
			{
				success: boolean;
				message: string;
				certificate_status: string;
				cloudfront_update_triggered: boolean;
				domain: Domain;
			},
			string
		>({
			query: (domainId) => ({
				url: `/domains/${domainId}/check-certificate-and-update-cloudfront`,
				method: 'POST',
			}),
			invalidatesTags: (result, error, domainId) => [
				{ type: 'Domains', id: result?.domain.tenant_id },
			],
		}),
	}),
});

export const {
	useGetTenantDomainsQuery,
	useGetVerificationInstructionsQuery,
	useVerifyDomainMutation,
	useGetCertificateValidationInstructionsQuery,
	useCheckCertificateAndUpdateCloudFrontMutation,
} = domainsApi;
